import React, { CSSProperties } from 'react';
import cls from 'classnames';
import { history, useDispatch, useIntl, useSelector } from 'umi';
import useFilterExtend from '@intelligence/hooks/useFilterExtend';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import TipContext from '@home/components/TipContext';
import TagItem from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/TagItem';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import steamIcon from '@intelligence/assets/svg/dataSource/steam.svg';
import igdbIcon from '@intelligence/assets/svg/gameInfo/igdb.svg';
import { saveFilterName } from '@intelligence/components/Filter/util';
import { namespace as HOME_NAMESPACE } from '@home/models/home';
import type { HomeModelStateType } from '@home/models/home';

import './index.scss';

interface ITagCard {
  type: 'steam_tag' | 'game_mode' | 'game_engine';
  title: React.ReactNode;
  tipContent?: React.ReactNode;
  tagList: string[];
  localeTextMap: { [key: string]: string };
  AWidth?: number;
  style?: CSSProperties;
  entityType?: 'mobile' | 'pc' | 'console' | 'combine';
  compact?: boolean;
}

function GameInfoTagCard({
  type,
  title,
  AWidth,
  tipContent,
  tagList,
  localeTextMap,
  style,
  entityType = 'combine',
  compact = false,
}: ITagCard) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const { userInfo } = useSelector<any, HomeModelStateType>(
    (state) => state?.[HOME_NAMESPACE] || {},
  );

  const { filterExtend, setFilterExtend } = useFilterExtend({});

  // Determine the icon based on type
  const iconSrc = type === filterKeyMap.GAME_ENGINE ? igdbIcon : steamIcon;

  // Determine route based on entity type
  const getTargetRoute = (): string => {
    if (entityType === 'mobile') {
      return '/intelligence/topCharts/mobileGames';
    }
    return '/intelligence/topCharts/pcConsoleGames';
  };

  const handleTagItemClick = (tag: string) => {
    // Reset other tag filters when clicking a tag
    const resetTypes = [
      filterKeyMap.STEAM_TAGS,
      filterKeyMap.GAME_MODE,
      filterKeyMap.GAME_ENGINE,
    ].filter((key) => key !== type);

    const resetFilters = resetTypes.reduce((acc, key) => {
      acc[key] = { value: [] };
      return acc;
    }, {} as { [key: string]: { value: string[] } });

    const newFilterExtend = {
      ...filterExtend,
      // 携带当前点击卡片 tag
      [type]: {
        value: [tag],
        ...(type === filterKeyMap.GAME_ENGINE ? { logic: 'OR' } : {}),
      },
      // 重置其他 tag
      ...resetFilters,
    };

    console.log('[GameInfoTagCard] Clicking tag:', tag);
    console.log('[GameInfoTagCard] Type:', type);
    console.log('[GameInfoTagCard] New filterExtend:', newFilterExtend);
    console.log('[GameInfoTagCard] UserInfo:', userInfo);

    // Use direct dispatch to ensure state update
    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveTopchartFilterExtendCommon`,
      payload: newFilterExtend,
    });
    
    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveIsOperate`,
      payload: true,
    });

    // Also update detail filter extend to match useFilterExtend behavior
    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveDetailFilterExtendCommon`,
      payload: newFilterExtend,
    });
    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveIsOperateDetail`,
      payload: true,
    });

    // Clear saved filter name to prevent it from overriding the clicked tag
    saveFilterName(userInfo as any, '');
    console.log('[GameInfoTagCard] Cleared saved filter name');

    dispatch({
      type: `${FILTER_EXTEND_NAMESPACE}/saveIsClickSteamOrGameModeTag`,
      payload: true,
    });

    console.log('[GameInfoTagCard] Navigating to:', getTargetRoute());
    
    // Use URL query params to pass tag info (more reliable than history.state)
    const queryParams = new URLSearchParams();
    queryParams.set('fromGameInfoTag', 'true');
    queryParams.set('tagType', type);
    queryParams.set('tagValue', tag);
    
    history.push(`${getTargetRoute()}?${queryParams.toString()}`);
  };

  return (
    <div className={cls('dbi_tag_card_wrapper', { 'dbi_tag_card_wrapper--compact': compact })} style={style}>
      <div className="dbi_tag_card_title_content">
        <img src={iconSrc} alt="" />
        <div className="dbi_tag_card_title">{title}</div>
        {tipContent && <TipContext AWidth={AWidth} tipText={tipContent} />}
      </div>

      <div className="dbi_tag_card_tags">
        {!tagList?.length && (
          <div className="dbi_tag_card_no_data">{formatMessage({ id: 'dbi_search_empty' })}</div>
        )}
        {tagList?.map((tag, index) => {
          return (
            <TagItem
              key={index}
              text={localeTextMap[tag] || tag}
              onClick={() => handleTagItemClick(tag)}
            />
          );
        })}
      </div>
    </div>
  );
}

export default GameInfoTagCard;
